import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const [BLUE, ORANGE] = CHART_COLORS;
const STRING = 130;
const BOB = 13;

/** Trig results can differ in the last digit between Node and the browser; round them. */
const round = (value: number): number => Math.round(value * 100) / 100;

/** A pendulum drawn at its release angle, with the mirror position it swings to. */
export function PendulumDiagram({
  id,
  label,
  description,
  degrees,
}: {
  id: string;
  label: string;
  description: string;
  /** Release angle in degrees; clamped to 1–179 for drawing. */
  degrees: number;
}) {
  const angle = (Math.min(Math.max(degrees, 1), 179) * Math.PI) / 180;
  const pivotX = 280;
  const pivotY = round(28 + Math.max(0, -Math.cos(angle)) * STRING);
  const height = Math.round(pivotY + STRING + 36);
  const bob = (side: 1 | -1) => ({
    x: round(pivotX + side * STRING * Math.sin(angle)),
    y: round(pivotY + STRING * Math.cos(angle)),
  });
  const right = bob(1);
  const left = bob(-1);
  const arcRadius = 52;
  const arcEnd = {
    x: round(pivotX + arcRadius * Math.sin(angle)),
    y: round(pivotY + arcRadius * Math.cos(angle)),
  };
  return (
    <ChartSvg
      description={description}
      height={height}
      id={id}
      label={label}
      width={560}
    >
      <line
        stroke={CHART_INK.axis}
        strokeDasharray="5 4"
        x1={pivotX}
        x2={pivotX}
        y1={pivotY}
        y2={pivotY + STRING + 12}
      />
      <line
        stroke={CHART_INK.grid}
        strokeWidth="2"
        x1={pivotX}
        x2={left.x}
        y1={pivotY}
        y2={left.y}
      />
      <circle cx={left.x} cy={left.y} fill={BLUE} fillOpacity="0.25" r={BOB} />
      <line
        stroke={CHART_INK.text}
        strokeWidth="2"
        x1={pivotX}
        x2={right.x}
        y1={pivotY}
        y2={right.y}
      />
      <circle cx={right.x} cy={right.y} fill={ORANGE} r={BOB} />
      <path
        d={`M ${pivotX} ${pivotY + arcRadius} A ${arcRadius} ${arcRadius} 0 0 0 ${arcEnd.x} ${arcEnd.y}`}
        fill="none"
        stroke={CHART_INK.muted}
        strokeWidth="1.5"
      />
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={round(pivotX + 22 + 16 * Math.sin(angle / 2))}
        y={pivotY + arcRadius + 34}
      >
        θ₀
      </text>
      <text
        fill={CHART_INK.text}
        fontSize="15"
        fontStyle="italic"
        textAnchor="middle"
        x={round((pivotX + right.x) / 2 + 14)}
        y={round((pivotY + right.y) / 2 - 18)}
      >
        L
      </text>
      <rect
        fill={CHART_INK.text}
        height="5"
        width="64"
        x={pivotX - 32}
        y={pivotY - 5}
      />
    </ChartSvg>
  );
}
